/**
 * Refunds — live refund and dispute case decisions.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHead } from "../components/PageHead";
import { EmptyState, ErrorCard, LoadingCard } from "../components/Async";
import { useAdminQuery, shortDateTime, tzs, errorMessage } from "../lib/admin";
import { adminApi } from "../lib/api";
import type { RefundCase } from "../lib/types";
import { Search, AlertTriangle, Check, X } from "lucide-react";

const statusColor: Record<string, string> = {
  open: "var(--ev-amber-600)",
  investigating: "var(--ev-blue-600)",
  resolved: "var(--ev-mint-600)",
  rejected: "var(--ev-red-600)",
  closed: "var(--ev-slate-500)",
};

interface RefundResponse {
  data: RefundCase[];
  total: number;
}

export function Refunds() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [decisionFor, setDecisionFor] = useState<{ ref: string; decision: "approve" | "reject" } | null>(null);
  const [note, setNote] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);

  const list = useAdminQuery<RefundResponse>(["refunds", statusFilter], "/admin/v1/refunds", {
    status: statusFilter === "all" ? undefined : statusFilter,
  });

  const decide = useMutation({
    mutationFn: (input: { ref: string; decision: "approve" | "reject"; note: string }) =>
      adminApi.post(`/admin/v1/refunds/${input.ref}/decision`, undefined, {
        query: { decision: input.decision, note: input.note },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin"] });
      setDecisionFor(null);
      setNote("");
      setActionError(null);
    },
    onError: (error) => setActionError(errorMessage(error)),
  });

  const rows = useMemo(() => {
    const data = list.data?.data ?? [];
    if (!search) return data;
    const q = search.toLowerCase();
    return data.filter(
      (c) =>
        c.ref.toLowerCase().includes(q) ||
        (c.agent?.business_name ?? "").toLowerCase().includes(q) ||
        (c.order_id ?? "").toLowerCase().includes(q),
    );
  }, [list.data, search]);

  return (
    <>
      <PageHead
        title="Refunds & Disputes"
        description="Case management, evidence review, and linked reversals."
      />

      <div className="admin-card-header admin-filter-bar" style={{ marginBottom: 16 }}>
        <div className="admin-filters" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", flex: 1 }}>
          <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
            <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ev-slate-400)", pointerEvents: "none" }} />
            <input
              className="ev-input"
              placeholder="Search case, agent, order…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 36 }}
            />
          </div>
          <select className="ev-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All status</option>
            <option value="open">Open</option>
            <option value="investigating">Investigating</option>
            <option value="resolved">Resolved</option>
            <option value="rejected">Rejected</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {actionError && (
        <div className="admin-note" style={{ marginBottom: 12 }}>
          <AlertTriangle size={16} />
          <span>{actionError}</span>
        </div>
      )}

      {list.isLoading && <LoadingCard label="Loading cases…" />}
      {list.error && <ErrorCard error={list.error} onRetry={() => void list.refetch()} />}
      {list.data && rows.length === 0 && <EmptyState>No cases match this filter.</EmptyState>}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {rows.map((c) => {
          const open = c.status === "open" || c.status === "investigating";
          return (
            <div key={c.ref} className="admin-card" style={{ padding: 20 }}>
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ width: 40, height: 40, borderRadius: "var(--ev-radius-md)", background: "var(--ev-amber-100)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <AlertTriangle size={18} style={{ color: "var(--ev-amber-600)" }} />
                </div>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                    <span className="ev-body-semibold">{c.ref}</span>
                    <span className="ev-badge" style={{ color: statusColor[c.status], background: `${statusColor[c.status]}20` }}>
                      {c.status}
                    </span>
                    <span className="ev-badge ev-badge-blue">{c.case_type}</span>
                  </div>
                  <div className="ev-body">{c.description || c.reason_code}</div>
                  <div className="ev-body-micro muted" style={{ marginTop: 4 }}>
                    Order <strong>{c.order_id ?? "—"}</strong> · <strong>{c.agent?.business_name ?? "—"}</strong> · {shortDateTime(c.created_at)}
                  </div>

                  {decisionFor?.ref === c.ref && (
                    <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                      <input
                        className="ev-input"
                        placeholder="Decision note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        style={{ flex: 1, minWidth: 200 }}
                      />
                      <button
                        className="ev-btn ev-btn-sm ev-btn-primary"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ ref: c.ref, decision: decisionFor.decision, note })}
                      >
                        Confirm {decisionFor.decision}
                      </button>
                      <button className="ev-btn ev-btn-sm ev-btn-ghost" onClick={() => { setDecisionFor(null); setNote(""); }}>
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div className="ev-body-semibold">{tzs(c.amount_minor)}</div>
                  {open && !decisionFor && (
                    <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                      <button className="ev-btn ev-btn-sm ev-btn-outline" title="Approve refund" onClick={() => { setDecisionFor({ ref: c.ref, decision: "approve" }); setNote(""); }}>
                        <Check size={16} />
                      </button>
                      <button className="ev-btn ev-btn-sm ev-btn-outline" title="Reject refund" onClick={() => { setDecisionFor({ ref: c.ref, decision: "reject" }); setNote(""); }}>
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
